/**
 * Notification log page — `GET /notifications`.
 *
 * Follows the CoOrdio list-page pattern established by {@link ProjectListPage}: `ShellHeader`,
 * `CordioAbpPagedDataTable`, and a BEM block named after the page slug. Two deliberate differences,
 * both because of what this page *is*:
 *
 *   1. No primary action and no `DeleteDialog`. The notification log is append-only — the API has no
 *      create and no delete — so a "New notification" button would be an invented capability.
 *   2. No toolbar search and no status tabs. `GET /notifications` accepts `projectId`, `type`,
 *      `page` and `pageSize` — nothing else (see `NotificationLogsController`). A search box would
 *      write a `searchTerm` the endpoint drops, and a Sent/Failed tab would write a `status` it does
 *      not have: controls that look like they filter but do not. Both dimensions the endpoint *does*
 *      support are in the filter popover instead.
 *
 * ── COLUMN PLAN (field → header → width → cell type → filter) ──────────────────────────────────
 *   sentAt           → Date       → 18% → text via valueGetter, locale date + time → none
 *   notificationType → Type       → 16% → custom Chip (outlined)                   → popover select `type`
 *   subject          → Subject    → 30% → text, maxLength 90 + showReadMore        → none
 *   recipients       → Recipients → 20% → text, maxLength 60 + showReadMore        → none
 *   status           → Status     → 16% → custom Chip (Sent / Failed / Pending)    → none (not filterable)
 *                                         (widths sum to 100%)
 *
 * The `projectId` popover filter has no column of its own: the roster of projects is in the shell's
 * switcher, and a column of repeated project names would spend 15% of the grid on the value the
 * reader just filtered by.
 *
 * DROPPED from the old MUI table: the leading expand/collapse chevron column and `htmlBody` as an
 * inline `<Collapse>` under each row. The grid owns its rows, so a page-level accordion is no longer
 * expressible — the email body moved to a row action that opens it in a dialog. That is also the
 * only shape that works against the real API: the list projection omits `HtmlBody` (only
 * `GET /notifications/{id}` returns it), so the old inline panel rendered an empty body.
 *
 * No column is `sortable`: the endpoint sorts by `SentAt DESC` and takes no `sortBy`.
 */

import { useCallback, useMemo, useState } from 'react';

import {
  Button,
  Chip,
  CordioAbpPagedDataTable,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  ShellHeader,
  type ChipColor,
  type CordioDataTableAction,
  type CordioDataTableColumn,
  type CordioDataTableListToolbarConfig,
} from '@coordio/ui';
import { EmptyState, InlineErrorPanel } from '@coordio/ui/layout';

import {
  createCortexPagedDataSource,
  type CortexPagedQuery,
} from '../components/data-display/cortexPagedDataSource';
import { sanitizeHtmlBody } from '../security/sanitizeHtmlBody';
import {
  getNotificationStatusLabel,
  getNotificationTypeLabel,
  notificationService,
  type NotificationLogDto,
} from '../services/notificationService';
import { useContextStore } from '../store/contextStore';

import './NotificationLogPage.scss';

/** The `NotificationType` enum values the endpoint binds, with the labels the service already uses. */
const NOTIFICATION_TYPE_FILTER_OPTIONS = [
  { label: 'Budget Alert', value: '0' },
  { label: 'Key Rotation', value: '1' },
  { label: 'Security Alert', value: '2' },
  { label: 'System Update', value: '3' },
] as const;

/** `NotificationStatus`: 0 Pending, 1 Sent, 2 Failed. */
function statusChipColor(status: number): ChipColor {
  if (status === 1) return 'green';
  if (status === 2) return 'red';
  return 'amber';
}

/** `NotificationType`: 0 Budget, 1 Key rotation, 2 Security, 3 System. */
function typeChipColor(type: number): ChipColor {
  switch (type) {
    case 0:
      return 'amber';
    case 1:
      return 'blue';
    case 2:
      return 'red';
    default:
      return 'default';
  }
}

/** A log entry's time matters to the minute, so this keeps the time the old page showed. */
function formatSentAt(value: string | undefined): string {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
}

/** The positional arguments `notificationService.getNotifications` takes, after the tenant id. */
type NotificationQueryArgs = {
  page: number;
  pageSize: number;
  projectId?: string;
  type?: number;
};

/**
 * Narrows the grid's query to exactly what the endpoint accepts.
 *
 * An explicit whitelist rather than a spread: `getNotifications` takes positional arguments, so a
 * filter it does not know about has nowhere to go, and silently dropping one is worse than never
 * offering it.
 */
function toNotificationQueryArgs(query: CortexPagedQuery): NotificationQueryArgs {
  const projectId = query.projectId;
  const type = Number(query.type);

  return {
    page: query.pageNumber,
    pageSize: query.pageSize,
    projectId: typeof projectId === 'string' && projectId ? projectId : undefined,
    type: query.type !== undefined && Number.isFinite(type) ? type : undefined,
  };
}

export const NotificationLogPage = () => {
  const currentTenant = useContextStore((s) => s.currentTenant);
  const availableProjects = useContextStore((s) => s.availableProjects);

  /** The row whose email body is open, or `null`. Read-only, so no `listVersion` is needed. */
  const [previewRow, setPreviewRow] = useState<NotificationLogDto | null>(null);
  const [previewBody, setPreviewBody] = useState<string | null>(null);

  const tenantId = currentTenant?.id;

  const dataSource = useMemo(
    () =>
      createCortexPagedDataSource<NotificationLogDto>(async (query) => {
        // Unreachable while the guard below renders instead of the grid; throwing rather than
        // silently resolving empty keeps a future regression visible.
        if (!tenantId) throw new Error('No workspace selected');

        const args = toNotificationQueryArgs(query);
        const result = await notificationService.getNotifications(
          tenantId,
          args.page,
          args.pageSize,
          args.projectId,
          args.type,
        );

        // `NotificationLogPageDto` is `items` + `totalCount` + `page` + `pageSize`; the adapter
        // wants Cortex's `PagedResult`, which adds the derived navigation flags.
        const totalPages = args.pageSize > 0 ? Math.ceil(result.totalCount / args.pageSize) : 0;
        return {
          items: result.items,
          totalCount: result.totalCount,
          pageNumber: result.page,
          pageSize: result.pageSize,
          totalPages,
          hasPreviousPage: result.page > 1,
          hasNextPage: result.page < totalPages,
        };
      }),
    [tenantId],
  );

  const columns = useMemo<ReadonlyArray<CordioDataTableColumn<NotificationLogDto>>>(
    () => [
      {
        field: 'sentAt',
        header: 'Date',
        type: 'text',
        width: '18%',
        valueGetter: (row) => formatSentAt(row.sentAt),
      },
      {
        field: 'notificationType',
        header: 'Type',
        type: 'custom',
        width: '16%',
        customCell: (row) => (
          <Chip
            color={typeChipColor(row.notificationType)}
            variant="outlined"
            label={getNotificationTypeLabel(row.notificationType)}
          />
        ),
      },
      {
        field: 'subject',
        header: 'Subject',
        type: 'text',
        width: '30%',
        maxLength: 90,
        showReadMore: true,
      },
      {
        field: 'recipients',
        header: 'Recipients',
        type: 'text',
        width: '20%',
        maxLength: 60,
        showReadMore: true,
      },
      {
        field: 'status',
        header: 'Status',
        type: 'custom',
        width: '16%',
        // The label comes from the API's own status value via the service's label map.
        customCell: (row) => (
          <Chip color={statusChipColor(row.status)} label={getNotificationStatusLabel(row.status)} />
        ),
      },
    ],
    [],
  );

  const listToolbar = useMemo<CordioDataTableListToolbarConfig>(
    () => ({
      // Deliberately no `search` and no `statusTabs` — see the note at the top of the file.
      filters: [
        {
          key: 'projectId',
          label: 'Project',
          type: 'select',
          placeholder: 'All projects',
          options: availableProjects.map((project) => ({ label: project.name, value: project.id })),
        },
        {
          key: 'type',
          label: 'Type',
          type: 'select',
          placeholder: 'All types',
          options: [...NOTIFICATION_TYPE_FILTER_OPTIONS],
        },
      ],
      filterButtonLabel: 'Filter',
    }),
    [availableProjects],
  );

  const openPreview = useCallback(async (row: NotificationLogDto) => {
    setPreviewRow(row);

    // The list projection omits `HtmlBody`; only the by-id endpoint returns it. Use whatever the
    // row already carries, and fetch the rest only when it is missing.
    if (row.htmlBody) {
      setPreviewBody(row.htmlBody);
      return;
    }

    setPreviewBody(null);
    try {
      const full = await notificationService.getNotification(row.id);
      setPreviewBody(full.htmlBody ?? '');
    } catch {
      setPreviewBody('');
    }
  }, []);

  const closePreview = useCallback(() => {
    setPreviewRow(null);
    setPreviewBody(null);
  }, []);

  const actions = useMemo<ReadonlyArray<CordioDataTableAction<NotificationLogDto>>>(
    () => [
      {
        icon: 'eye',
        tooltip: 'View email body',
        onClick: (row) => void openPreview(row),
      },
    ],
    [openPreview],
  );

  if (!tenantId) {
    return (
      <div className="notification-log" data-testid="notification-list-page">
        <ShellHeader
          title="Notification Log"
          description="Every alert email this workspace has sent, with its delivery outcome."
          data-testid="notification-list-header"
        />
        <EmptyState
          title="No workspace selected"
          description="Choose a workspace from the user menu to see the notifications it has sent."
          data-testid="notification-empty-state"
        />
      </div>
    );
  }

  return (
    <div className="notification-log" data-testid="notification-list-page">
      <ShellHeader
        title="Notification Log"
        description={`Every alert email ${currentTenant.name} has sent, with its delivery outcome. This log is append-only.`}
        data-testid="notification-list-header"
      />

      <div data-testid="notification-list-grid">
        <CordioAbpPagedDataTable<NotificationLogDto>
          className="notification-log__table"
          columns={columns}
          dataSource={dataSource}
          initialPageSize={25}
          maxBodyHeight="520px"
          listToolbar={listToolbar}
          actions={actions}
          onRowClick={(row) => void openPreview(row)}
        />
      </div>

      {/* Replaces the old inline <Collapse> panel: the grid owns its rows, so the body opens over
          the list instead of inside it. */}
      <Dialog open={previewRow !== null} onOpenChange={(open) => { if (!open) closePreview(); }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{previewRow?.subject ?? 'Notification'}</DialogTitle>
          </DialogHeader>
          <span className="text-xs text-[var(--color-text-secondary)]">
            {previewRow ? `${formatSentAt(previewRow.sentAt)} · ${previewRow.recipients}` : null}
          </span>
          {previewRow?.errorMessage && (
            <InlineErrorPanel message={previewRow.errorMessage} data-testid="notification-log-preview-error" />
          )}
          <div
            className="notification-log__preview-body"
            data-testid="notification-log-preview-body"
            // The body is tenant-authored HTML; `sanitizeHtmlBody` is the SEC-H13 allowlist.
            dangerouslySetInnerHTML={{ __html: sanitizeHtmlBody(previewBody ?? '') }}
          />
          <DialogFooter>
            <Button type="button" variant="outline" onClick={closePreview}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
